import { Event } from "react-big-calendar";
import { MEETING_ROOM_FEATURES } from "./lib/constants";
import { AxiosError } from "axios";
import { ReactNode } from "react";
import { Option } from "@app-components";

export enum IntercomEvent {
  LOGIN_LINK_EXPIRED = "login-link-expired",
  PHONE_VALIDATION_SEEN = "phone-validation-seen",
  PHONE_VALIDATION_FAILED = "phone-validation-failed",
  PHONE_VALIDATION_SUCCESS = "phone-validation-success",
  MEETING_ROOM_BOOKED = "meeting-room-booked",
  NEWS_ARTICLE_PUBLISHED = "news-article-published",
  VISITOR_SMS_SENT = "visitor-sms-sent",
  VISITOR_ACCESS_GRANTED = "visitor-access-granted",
  USER_INVITED = "user-invited",
  ACCESS_CARD_DEACTIVATED = "access-card-deactivated",
  PIN_CODE_CHANGED = "pin-code-changed",
  ACCESS_CARD_PRINT = "access-card-print",
  ACCESS_CARD_DELETED = "access-card-deleted",
  SHOWN_SNACKS_AFTER_BOOKING = "shown-snacks-after-booking",
  STARTED_SNACK_ORDER_FROM_BOOKING = "started-snack-order-from-booking",
  STARTED_SNACK_ORDER = "started-snack-order",
  COMPLETED_SNACK_ORDER = "completed-snack-order",
}

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type NestedKeys<T, Prefix extends string = ""> = {
  [K in Extract<keyof T, string>]: NonNullable<T[K]> extends object
    ? `${Prefix}${K}` | NestedKeys<NonNullable<T[K]>, `${Prefix}${K}.`>
    : `${Prefix}${K}`;
}[Extract<keyof T, string>];

export type CustomAxiosError = AxiosError<{ code: string }>;

export type DateString = `${string}-${string}-${string}` & {
  __brand: "DateString";
};
export type TimeString = `${string}:${string}` & { __brand: "TimeString" };
export type MonthFilterValue = `${string}-${string}` & {
  __brand: "MonthFilterValue";
};
export type FromToDateFilterValue = { from: DateString; to: DateString };
export type TimeframeFilterValue = MonthFilterValue | FromToDateFilterValue;

/** Every module a tenant can reach in a building; the sidebar and the dashboard read the same list. */
export type BuildingModuleKey =
  | "home"
  | "news"
  | "visitors"
  | "access"
  | "handbook"
  | "bookings"
  | "lunch"
  | "orders"
  | "order_new"
  | "custom";

export enum SidebarWorkspace {
  USER = "USER",
  COMPANY = "COMPANY",
  BUILDING = "BUILDING",
  SNACK = "SNACK",
  FOOD = "FOOD",
  RESTAURANT = "RESTAURANT",
  SUPER = "SUPER",
  OVERVIEW = "OVERVIEW",
  SETTINGS = "SETTINGS",
}

export type DropdownOption<T = string> = {
  value?: T;
  label: string;
  group?: string;
};

export type MeetingRoomFeature = Omit<Option, "icon"> & {
  icon: string;
};

export enum NotificationType {
  ERROR = "error",
  SUCCESS = "success",
  INFO = "info",
  WARNING = "warning",
}

export type storeType = {
  notifications: {
    id: number;
    message: string | ReactNode;
    type: NotificationType;
    onClose?: () => void;
  }[];
  addNotification: (
    message: string | ReactNode,
    type?: NotificationType,
    persistent?: boolean,
    onClose?: () => void,
  ) => number;
  removeNotification: (id: number) => void;
};

export type BuildingUserStoreType = {
  user: User;
  building: string;
  setBuilding: (buildingId: string) => void;
  setUser: (user: User) => void;
  refetchUser: () => Promise<void>;
  setRefetchUser: (newRefetchUser: () => Promise<User>) => void;
};

export type AppReloadingLoadingType = {
  outletKey: number;
  bumpOutletKey: () => void;
};

export type contextStoreType = {
  selectedCompany: string;
  /** `buildingId` defaults to the building in the URL. */
  setCompany: (value: string, buildingId?: string) => void;
  /** Stores the selection for `buildingId` without selecting it now; it is
   * picked up by syncBuilding when that building is navigated to. */
  rememberCompany: (buildingId: string, value: string) => void;
  /** The building `selectedCompany` belongs to. */
  buildingId: string;
  /** Loads the company remembered for `buildingId` when the building changes
   * without going through setCompany, e.g. a redirect to the default one. */
  syncBuilding: (buildingId: string) => void;
};

export type articleStoreType = {
  storedArticle: ArticleFormType | null;
  hasGeneratedAiContent: boolean;
  aiImageOptions: ExportImageData[];
  setStoredArticle: (storedArticle: ArticleFormType | null) => void;
  setHasGeneratedAiContent: (hasGeneratedAiContent: boolean) => void;
  setAiImageOptions: (images: ExportImageData[]) => void;
};

export type Timestamp = {
  _seconds: number;
  _nanoseconds: number;
};

export interface CompanyVisit {
  id: string;
  entranceId: string;
  entranceName: string;
  buildingId: string;
  buildingTenantId: string;
  buildingTenantName: string;
  language: string; //host language
  messageFromVisitor: string;
  visitorPhone: string;
  visitorCompanyName: string;
  visitorId: string;
  visitorName: string;
  visitorLanguage?: string;
  createdAt: Timestamp;
  letInAt?: Timestamp;
  messageHistory?: MessageHistoryItem[];
  errorSendingAccessCode?: boolean;
}

export interface Visit extends CompanyVisit {
  hostId: string;
  hostPhone: string;
  hostName: string;
  hostEmail: string;
}

export interface MessageHistoryItem {
  senderId: string;
  senderName: string;
  body: string;
  timestamp: Timestamp;
  type?: string; // e.g. "sms", "comment" - used to distinguish between different message types
}

export enum AccessSystem {
  STARWATCH = "starwatch",
  LENEL = "lenel",
  ARX = "arx",
  SYMMETRY = "symmetry",
}

export enum MenuItemVisibility {
  USER = "user",
  COMPANY_ADMIN = "company_admin",
}

export type CustomMenuItem = {
  url: string;
  label: {
    en: string;
    no: string;
  };
  visibility: MenuItemVisibility;
};

export type Address = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type Building = {
  propertyManagerId: string; // userId of the property manager
  buildingBannerImgUrl: string;
  buildingAdmins: string[];
  buildingAccessAdmins?: string[];
  displayName: string;
  legalName: string;
  address?: Address;
  owner: string;
  ownerLogoImgUrl: string;
  defaultAccessCardPickupMsg?: {
    no: string;
    en: string;
  };
  bookingEnabled?: boolean;
  bookingVisibleForUsers?: boolean;
  bookingSettings?: {
    maxBookingsPerPerson?: number;
    maxDaysAhead?: number;
    maxDuration?: number;
    showNotesInput?: boolean;
  };
  openingHour?: TimeString;
  closingHour?: TimeString;
  handbookLink?: string;
  dashboardEnabledForAdmins?: boolean;
  dashboardEnabledForUsers?: boolean;
  hasMunu?: boolean;
  hasCommunication?: boolean;
  communicationVisibleForUsers?: boolean;
  accessControlSystem?: AccessSystem;
  // Feature flag for all Starwatch integration except the visitor flow
  starwatchIntegrationEnabled?: boolean;
  // Starwatch config for the building, entered by hand in Firestore. The portal
  // only checks that it's there.
  starwatch?: Record<string, unknown>;
  /** @deprecated Use accessControlSystem instead. */
  accessSystem?: AccessSystem;
  buildingAdminsNotifications?: string[]; // array of userIds who will be notified on access card order/cancellation
  buildingAdminsOperationsTeam?: string[];
  buildingAdminsNotifyOnBooking?: string[]; // array of userIds who will be notified on bookings
  notifyOnVisitorLabelPrintingFailure?: string[]; // array of userIds to notify when visitors report missing printed labels
  buildingReceptionists?: string[]; // array of userIds who are receptionists in the building
  accessControlSystemIntegration?: boolean; // If the building has an access control system integration with Sharry
  customMenuItems?: CustomMenuItem[];
  teamPhotoImgUrl?: string;
  customWelcomeMessage?: {
    en: string;
    no: string;
  };
  propertyOwner?: string; //This field should not be changed! Can be value of following strings: "Entra ASA", "Storebrand", "Malling" (Used for Sharry company creation)
  customAIInstructions?: string;
  hasMillum?: boolean; // If the building has Millum integration enabled
  millumGLN?: string; // GLN for the buildings canteen, used for integration with Millum
  allowBadgePrinting?: boolean; // If the building allows badge printing for building admins
  hasParking?: boolean; // If the building has parking enabled
  supplierCompanyId?: string;
  hasWelcomeSnack?: boolean;
  snackVisibleForUsers?: boolean;
  snackBlockedForCompanies?: string[]; // Ids of companies that are blocked from using Snack
  hasWelcomeFood?: boolean;
  foodWalletRefundEnabled?: boolean; // Super-settings flag for refund feature
  supplierSnackAdmins?: string[]; // whitelisted userIds granted the SupplierSnackAdmin role
  supplierFoodAdmins?: string[]; // whitelisted userIds granted the SupplierFoodAdmin role
  acsAllowForgotCardCodeGeneration?: boolean; // allow users to generate access codes when they forget their card
  runQuarterlyUserPurge?: boolean; // enable quarterly user purge for the building
  passentryTemplateId?: string; // Passentry template ID for the building's lunch passes
  allowBuildingAdminPinManagement?: boolean; // Allow building admins to set/reset PIN codes for users
  redirectToApp?: boolean;
  disablePlasticCardOrdering?: boolean; // If true, building admins will not be able to order physical access cards, only digital ones
  allowAccessRoleManagement?: boolean; // Feature flag, allows building admins to manage access roles in the building
  defigoBuildingId?: string;
  twoN?: TwoNDeviceConfig; // 2N intercom directory sync; written only via update2NConfig
  enableWelcomeVisit?: boolean; // Feature flag to enable welcome visit flow for the building
  visitorLabelPrintingEnabled?: boolean; // Capability flag for automatic visitor label printing. Missing value should be treated as false
  acsDigitalBadgeMode?: boolean; // Building-level gate for digital badges; when false/missing, company digital badge settings are overridden
  acsConnectivityGuardEnabled?: boolean; // Feature flag to enable ACS connectivity guard
  acsVisitorAccessCodeViaPoly?: boolean; // FUN-1214: per-building kill switch — generate visitor access codes via legacy Poly instead of GCP Sharry/Starwatch SDKs
  acsConnectivityLost?: boolean; // true when ACS connectivity is down; blocks access operations in the UI
  acsConnectivityLostMessage?: {
    no: string;
    en: string;
  }; // Optional custom message shown in the warning banner; falls back to default text if absent
};

// Building + property manager dropdown selection + fields for Base64 encoded images
export type BuildingForm = Omit<
  Building,
  "buildingAdmins" | "propertyManagerId"
> & {
  propertyManagerId?: {
    label: string;
    value: string;
  };
  teamPhotoImg: string;
  buildingBannerImg: string;
  ownerLogoImg: string;
};

export type BuildingManager = {
  firstName: string;
  lastName: string;
  profileImgUrl: string;
  jobTitle: string;
  companyName: string;
};

export type TwoNDeviceConfig = {
  entranceId: string;
  serial: string; // credentials live in Secret Manager as twon-device-<serial without dashes>
  sipAccount?: number; // not editable in the UI; defaults to 1
  enabled: boolean;
};

export type TwoNSyncResult = {
  building: string;
  serial: string;
  entranceId: string;
  dryRun: boolean;
  status: "planned" | "applied" | "aborted" | "skipped" | "failed";
  reason?: string;
  companies: { name: string; users: number; entries: number }[];
  desired: number;
  onDevice: number;
  foreign: number;
  skippedNoPhone: number;
  skippedNoName: number;
  create: string[];
  update: string[];
  delete: string[];
  failures: string[];
};

export type Entrance = {
  entranceId?: string;
  entranceName: {
    no: string;
    en: string;
  };
  qrCodeLink?: string;
  address: string;
};

export interface User {
  buildingTenantId: string;
  // Set while the user is managed through the company's SCIM provisioning.
  scim?: { companyId: string; externalId?: string };
  adminInCompanies: string[];
  accessAdminInCompanies?: string[];
  email: string;
  phone: string;
  phoneNumberValidated?: boolean;
  exemptFromPhoneNumberValidation?: boolean;
  subdivisionId?: string;
  jobTitle?: string;
  customEmployeeId?: string;
  userId: string;
  firstName: string;
  lastName: string;
  visibleInVisit: boolean;
  language: string;
  generalConsent: boolean;
  userActive?: boolean;
  userProfileImgUrl?: string;
  companyContact?: boolean;
  activeAccessCardNumber?: string | null;
  notificationPreferences?: { sms: boolean; email: boolean };
  sharryUserId?: string;
  starwatchUserId?: number;
  integratedWithAccessControlSystem?: boolean;
  integratedWithWelcomeFood?: boolean;
  createdAt?: Date;
  lastLogin?: Date;
  resignationDate?: Date;
  lastActiveDate?: Timestamp;
  disabled?: boolean;
}

export type MinimalUser = Pick<
  User,
  | "userId"
  | "firstName"
  | "lastName"
  | "phone"
  | "email"
  | "buildingTenantId"
  | "subdivisionId"
  | "userProfileImgUrl"
  | "customEmployeeId"
  | "jobTitle"
>;

export interface UserAuthMetadata {
  userId: string;
  createdAt?: Date;
  lastLogin?: Date | null;
  disabled?: boolean;
}

export type UserWithMetadata = User & UserAuthMetadata;

export interface UserDetailsForm extends Omit<
  User,
  | "buildingTenantId"
  | "userId"
  | "adminInCompanies"
  | "generalConsent"
  | "phone"
> {
  userProfileImg?: string; // Base64 encoded image
  phone?: string; // Optional phone number, can be empty under certain circumstances
  notificationPreferences: { sms: boolean; email: boolean };
}

export interface ExtendedUser extends User {
  buildingTenantName: string;
}

export interface RestrictiveExtendedUser {
  userId?: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  email?: string;
  phone?: string;
  buildingTenantName: string;
  userProfileImgUrl?: string;
}

export interface CompanyContact {
  userId: string;
  name: string;
  email: string;
  userProfileImgUrl?: string;
  jobTitle?: string;
}

export enum ConsentType {
  GENERAL = "general",
}

export type Consent = {
  id?: string;
  type: ConsentType;
  userId: string;
  consented: boolean;
  timestamp: Timestamp;
};

export type UpdateConsent = {
  type: ConsentType;
  consented: boolean;
};

export enum VisitMode {
  ENABLED = "enabled",
  LIMITED = "limited",
  DISABLED = "disabled",
}

export enum VisitorLabelPrintMode {
  OFF = "off",
  AUTO = "auto",
  MANUAL = "manual",
}

export type SSOItem = {
  provider: string;
  domain: string;
};

export enum CompanyType {
  TENANT = "Tenant",
  OWNER = "Owner",
  SERVICE_PROVIDER = "Service_Provider",
  EXTERNAL = "External",
  SUPPLIER = "Supplier",
}

export type CompanyFoodDeal = {
  dealId: string;
  default?: boolean; // true if this is the default deal for the company
  maxUsers?: number; // per-deal user limit (undefined = unlimited)
  isGuestDeal?: boolean; // mirrored from supplier deal
};

export interface Company {
  buildingTenantId: string;
  buildingTenantName: string;
  entranceIds: string[];
  floor?: string;
  instructions?: { en: string; no: string };
  companyLogoImgUrl?: string;
  orgNumber: string;
  ssoSettings: SSOItem[];
  visitMode: VisitMode;
  visitorLabelPrintMode?: VisitorLabelPrintMode; // Missing value should be treated as off
  visitOrder?: number;
  customLetInMessage?: string;
  accessControlSystemIntegration?: boolean; // If the company has an access control system integration with Sharry
  bookingDiscountMultiplier?: number;
  foodDeals?: CompanyFoodDeal[]; // array of deal IDs from Welcome Food (missing or empty if not integrated)
  foodMaxIntegratedUsers?: number; // max number of users that can be integrated with the food system
  lunchCreditAmount?: number; // monthly lunch credit granted to all the company's Food users (except those with guest deal)
  hasSubdivisionLink?: boolean; // when true, show subdivision linking UI instead of parent company linking (Munu)
  acsAutomaticProvisioningMode?: boolean; //flag if user should be created automatically
  acsDigitalBadgeMode?: boolean; // enable users to generate digital badges
  acsSendDigitalAccessEmailOnIntegration: boolean; // flag to determine if a digital access email should be sent on user integration (only is acsDigitalBadgeMode is enabled)
  acsDisableAccessCodeGeneration?: boolean; // disable access code generation in this company (visitors and forgot card functionality)
  acsAllowCompanyAdminsCardCreation?: boolean; // allow company admins to create access cards for users in this company
  acsAllowCompanyAdminsCardPrinting?: boolean; // allow company admins to print access cards for users in this company
  acsAllowCompanyAdminsPinManagement?: boolean; // allow company admins to set/reset PIN codes for users in this company who have never signed in
  companyType?: CompanyType;
  notifyOnCompanyVisit?: string[]; // array of userIds who will be notified on company visits
  notifyOnAccessCardDeletion?: string[]; // array of userIds who will be notified on access card deletion
  createdAt?: Timestamp;
  notifyUsersOfInactivity?: boolean; // If company wants their regular users to receive inactivity notifications before every quarterly user purge
  snackOnlyAllowAdminsToInvoice?: boolean; // If true, we don't allow regular users of the company to pay for Snack orders using invoice - default false/undefined
  // Temporary flag to enable adding users for companies that are not fully migrated yet
  scimEnabled?: boolean; // Super-admin toggle for the SCIM user provisioning API; company-wide (all buildings)
  skipSameCompanyUserDuplicationCheck?: boolean; // If true, we allow users to exist in different companies in different buildings (skipping the same-company-in-different-building check). When absent, treat as false and only allow the override when explicitly enabled. We will only check if the user already exists in this building
}

export interface ExtendedCompany extends Company {
  totalUsers: number;
  activeUsers: number;
}

export type SimpleFoodCompany = Pick<
  Company,
  | "buildingTenantId"
  | "buildingTenantName"
  | "foodDeals"
  | "foodMaxIntegratedUsers"
  | "lunchCreditAmount"
>;

export type FoodCompany = SimpleFoodCompany & {
  totalUsersCount: number;
  integratedUsersCount: number;
  disabledUsersCount: number;
  nonIntegratedUsersCount: number; // total - integrated - disabled = nonIntegrated
  usersCountPerDeal?: { [dealId: string]: number };
};

export type FoodDeal = {
  dealId: string;
  name: string;
  lunchVariantId: string;
  lunchVariant?: LunchVariant; // appended on in frontend if found
  price: number;
  subsidyPercentage?: number;
  isGuest?: boolean;
  createdAt: Timestamp;
};

export type CreateDealForm = Omit<
  Optional<FoodDeal, "lunchVariantId">,
  "dealId" | "createdAt"
>;

export type FoodUser = {
  userId: string;
  companyId: string;
  dealIds: string[];
  pendingTransactions: PendingFoodTransaction[];
  audit: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
  };
  currentBalance: number;
  currentCreditBalance?: number;
  history: FoodTransaction[];
  disabled?: boolean;
  userNotFound?: boolean;
};

export type DisabledFoodWallet = {
  userId: string;
  userEmail: string;
  companyId: string;
  companyName?: string;
  currentBalance: number;
  refundableAmount: number;
  refundRequiresSupport: boolean;
  pendingRefund?: {
    amount: number;
    status: string;
    refundTransactionId: string;
  };
};

export type RefundFoodBalanceResponse = {
  refundTransactionId: string;
  status: string;
  amount: number;
  paymentId: string;
  currentBalance: number;
};

export type UsersFoodDeal = {
  dealIds: string[];
  hasFreeDeal: boolean;
  requiresBalance: boolean;
  currentBalance?: number; // not returned for company admins
  userNotFound?: boolean;
};

export type FoodUserBalance = {
  userId: string;
  email: string;
  companyName: string | null;
  balance: number;
};

export type FoodUserBalancesResponse = {
  asOfDate: string;
  users: FoodUserBalance[];
  summary: { totalUsers: number; totalBalance: number };
};

export enum FoodTransactionStatus {
  PENDING = "pending", // payment begun, unknown whether it will be successful or fail
  PAID = "paid", // payment succesful, balance will be updated
  FAILED = "failed", // payment failed
  EXPIRED = "expired", // payment link expired
  OPEN = "open", // payment link created
  CANCELED = "canceled", // payment link canceled
  AUTHORIZED = "authorized", // payment successful - but the actual money has not been transferred yet
}

export interface PendingFoodTransaction {
  paymentId: string; // Mollie payment ID
  amount: number; // Amount in NOK
  status: FoodTransactionStatus; //The status from Mollie
  idempotencyKey: string; // For preventing duplicates payments
  createdAt: Timestamp; // When payment initiated
  type?: "topup" | "refund"; // Present for refunds awaiting settlement
  refundTransactionId?: string; // Mollie refund ID
}

export interface FoodTransaction {
  timestamp: Timestamp; // When transaction occurred
  type: string; // "topup" or "eat-{buildingId}"
  amount: number; // Positive for topup, negative for eating
  paymentId?: string; // Only for topup transactions
  eventId?: string; // Only for eat transactions
  status: FoodTransactionStatus;
  subsidyPercentage?: number;
  paidFromCredit?: number;
  paidFromBalance?: number;
  dealPrice?: number;
  dealId?: string;
  refundTransactionId?: string; // Mollie refund ID
  description?: string; // Free-text note
}

export interface FoodAdminTopupTransaction {
  type: "topup";
  transactionId: string;
  date: string;
  amount: number;
  userId: string;
  companyName: string;
}

export interface FoodAdminRefundTransaction extends Omit<
  FoodAdminTopupTransaction,
  "type"
> {
  type: "refund";
  paymentId?: string;
  refundTransactionId?: string;
  description?: string;
}

export interface FoodAdminLunchTransaction extends Omit<
  FoodAdminTopupTransaction,
  "type"
> {
  type: "lunch";
  dealId: string;
  dealPrice: number;
  lunchVariantName: string;
  subsidyPercentage: number;
  companySubsidy: number;
  paidFromCredit: number;
  paidFromBalance: number;
}

// Paid by a guest through Mollie checkout, not from a wallet
export interface FoodAdminGuestLunchTransaction {
  type: "guest-lunch";
  transactionId: string;
  date: string;
  amount: number; // total paid for all guestCount covers
  guestId: string;
  guestLunchGrossPrice: number; // per cover, as charged
  paidFromBalance: number;
  guestCount: number;
  vatAmount: number; // for the whole payment
  userId?: never;
  companyName?: never;
}

export type FoodAdminTransaction =
  | FoodAdminTopupTransaction
  | FoodAdminLunchTransaction
  | FoodAdminRefundTransaction
  | FoodAdminGuestLunchTransaction;

export type FoodAdminWalletTransaction = Exclude<
  FoodAdminTransaction,
  FoodAdminGuestLunchTransaction
>;

export interface FoodAdminOverview {
  transactions: FoodAdminTransaction[];
  summary: {
    lunchCount: number;
    averageDealPrice: number;
    totalSales: number;
    totalCompanySubsidy: number;
    totalPaidFromCredit: number;
    totalPaidFromBalance: number;
    totalCompanyShare: number;
    totalEmployeeShare: number;
    topupCount: number;
    totalAmountTopup: number;
    totalUnusedBalance: number;
    guestLunchPaymentCount?: number;
    guestLunchGuestCount?: number;
    totalGuestLunchRevenue?: number;
  };
}

export type InsightsSummaryKey =
  | "lunchCount"
  | "averageDealPrice"
  | "totalSales"
  | "totalCompanySubsidy"
  | "totalPaidFromCredit"
  | "totalPaidFromBalance"
  | "totalCompanyShare"
  | "totalEmployeeShare"
  | "topupCount"
  | "totalAmountTopup"
  | "totalUnusedBalance"
  | "guestLunchPaymentCount"
  | "guestLunchGuestCount"
  | "totalGuestLunchRevenue";

export type InsightsCountFormatter = "number" | "currency";

export type InsightsCountTile = {
  id: string;
  kind: "count";
  label: string;
  summaryKey: InsightsSummaryKey;
  formatter: InsightsCountFormatter;
};

export type InsightsChartType = "bar" | "line" | "area" | "pie";
export type InsightsGroupBy = "date" | "company" | "variant" | "paymentType";
export type InsightsValueKey = "count" | "totalAmount" | "subsidy";
export type InsightsChartScope = "all" | "lunch" | "guest";

export type InsightsChartTile = {
  id: string;
  kind: "chart";
  title: string;
  chartType: InsightsChartType;
  groupBy: InsightsGroupBy;
  breakdownBy?: InsightsGroupBy;
  valueKey: InsightsValueKey;
  /** Defaults to "lunch"; "all" and "guest" charts are per day only */
  scope?: InsightsChartScope;
};

export type InsightsTileConfig = InsightsCountTile | InsightsChartTile;

type InsightsLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type InsightsDashboardLayout = {
  tiles: InsightsTileConfig[];
  breakpointLayouts?: Partial<Record<string, InsightsLayoutItem[]>>;
};

export interface Subdivision {
  subdivisionId: string;
  companyId: string;
  name: string;
  orgNumber?: string;
  invoiceReferences?: string[];
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  assignedUsersCount?: number;
}

export type SelectChild = {
  value: string;
  label: string;
};

export type AddUserType = {
  firstName: string;
  lastName: string;
  adminInCompanies: string[];
  email: string;
  phone?: string;
  customEmployeeId?: string;
  visibleInVisit: boolean;
  companyContact?: boolean;
  sso?: boolean;
  sendEmail: boolean;
  language?: string;
  jobTitle?: string;
  subdivisionId?: string;
  selectedFoodDeals?: string[];
};

export type CsvData = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
};

export type BronnoysundCompanyResult = {
  organisasjonsnummer: string;
  navn: string;
};

export enum CardType {
  PHYSICAL = "physical",
  DIGITAL = "digital",
}

export enum CardStatus {
  ORDERED = "ordered",
  CANCELLED = "cancelled", // order cancelled
  COMPLETED = "completed", // order completed
}

export type CardOrderedBy = {
  userId: string;
  company: string;
  name?: string;
  email?: string;
};

export type AccessCardOrderType = {
  id: string;
  type: CardType;
  orderedBy: CardOrderedBy;
  orderNote?: string;
  status: CardStatus;
  createdAt: Timestamp;
  activatedAt?: Timestamp;
  cancelledAt?: Timestamp;
  cardNumber?: string;
  userId: string;
  userName?: string;
  pickupInstructions?: string;
  note?: string;
};

export type AccessCardHistory = {
  cardId: string;
  status: CardStatus;
  timestamp: Timestamp;
  modifiedByUser?: {
    firstName?: string;
    lastName?: string;
    buildingTenantName?: string;
  };
};

export type PreRegistrationAttendee = {
  name: string;
  phone: string;
  email?: string;
  arrivedAt?: Timestamp;
  errorSendingAccessCode?: boolean;
  checkedInById?: string;
  checkedInByName?: string;
  language?: "no" | "en";
};

export type PreRegistrationGroup = {
  id: string;
  hostId: string;
  hostName?: string;
  hostPhone?: string;
  hostEmail: string;
  companyName: string;
  companyId: string;
  eventName: string;
  eventDescription?: string;
  startTime: Timestamp;
  notifyOnEveryArrival: boolean;
  attendees: PreRegistrationAttendee[];
  entranceId: string;
  createdAt?: never; // to more easily distinguish between regular and pre-reg visitors
};

export type ExtendedPreRegistrationAttendee = {
  name: string;
  phone: string;
  email?: string;
  arrivedAt?: Timestamp;
  startTime: Timestamp;
  hostName?: string;
  hostPhone?: string;
  hostEmail: string;
  hostId: string;
  eventName: string;
  eventDescription?: string;
  companyName: string;
  companyId: string;
  eventId: string;
  errorSendingAccessCode?: boolean;
  checkedInById?: string;
  checkedInByName?: string;
};

export type Visitor = Visit | ExtendedPreRegistrationAttendee;

export enum BookableResourceType {
  MEETING_ROOM = "MEETING_ROOM",
  CONFERENCE_ROOM = "CONFERENCE_ROOM",
}

export type BookableResource = {
  roomId: string;
  roomName: string;
  description: {
    en: string;
    no: string;
  };
  type: BookableResourceType;
};

export type MeetingRoomFeatures = (keyof typeof MEETING_ROOM_FEATURES)[];

export enum MeetingRoomPricingModel {
  FREE = "free",
  HOURLY = "hourly",
}

export enum MeetingRoomBookingPermission {
  ALL = "all",
  COMPANY_ADMIN = "company_admin",
  BUILDING_ADMIN = "building_admin",
}

export type MeetingRoom = BookableResource & {
  subRoomIds?: string[];
  floor: string | number;
  location?: {
    en: string;
    no: string;
  };
  customReminderMessage?: {
    en: string;
    no: string;
  };
  features: MeetingRoomFeatures;
  capacity: number;
  thumbnail?: string;
  images?: string[];
  hourlyPrice?: number;
  maxPricePerBooking?: number;
  fixedBookingFee?: number;
  opensAt?: TimeString;
  closesAt?: TimeString;
  closedOnDays?: number[];
  blockBookingOn?: BlockedTimeSlot[];
  /**
   * Replace the four fields above for users in External companies. Admins get
   * the raw values; other callers get them already swapped in by the proxy.
   * Set or cleared together, `null` means not set.
   */
  opensAtForExternal?: TimeString | null;
  closesAtForExternal?: TimeString | null;
  closedOnDaysForExternal?: number[] | null;
  blockBookingOnForExternal?: BlockedTimeSlot[] | null;
  meetingFoodUrl?: string;
  allowBookingFor: MeetingRoomBookingPermission;
  hideEventTitles: boolean;
  bufferTimeInHours?: number;
};

export type BlockedTimeSlot = {
  weekday: number; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  from: TimeString; // e.g. "11:00"
  to: TimeString; // e.g. "13:00"
};

export enum MeetingRoomBookingStatus {
  CONFIRMED = "confirmed",
  CANCELLED = "cancelled",
}

export type MeetingRoomBooking = {
  userId: string;
  id: string;
  google_event_url: string;
  companyId: string;
  status: MeetingRoomBookingStatus;
  buildingId: string;
  roomId: string;
  roomName: string;
  notes?: string;
  event: {
    title: string;
    start: Date;
    end: Date;
  };
  participantsCount?: number;
  createdByUser?: {
    name: string;
    email?: string;
  };
  invoiceReference?: string | MultiFieldInvoiceReference;
  fixedBookingFee?: number;
  totalPrice?: number;
  hourlyPrice?: number;
  maxPricePerBooking?: number;
  companyDiscount?: number;
  duration?: number; // in hours
  createdTime: Date;
  lastEditTime: Date;
  showAsUpcoming: boolean;
};

export enum BookingStatus {
  CONFIRMED = "confirmed",
  CANCELLED = "cancelled",
}

export interface CalendarEvent extends Event {
  id?: string;
  isUsersBooking?: boolean;
  isFromLinked?: boolean;
  isBlocked?: boolean; // used for blocked time slots in meeting rooms
  isBuffer?: boolean; // used for optional buffer time between bookings
  isMergedLinked?: boolean; // several overlapping linked-room bookings shown as one unavailability band
  linkedTitles?: string[]; // the merged bookings' titles, listed under the band's headline
}

export type PreRegCalendarEvent = PreRegistrationGroup & Event;

export interface BookingAdminEvent extends Event {
  createdTime: Date;
  lastEditTime: Date;
  bookingId: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  companyId: string;
  companyName: string;
  roomId: string;
  roomName: string;
  status: BookingStatus;
  fixedBookingFee?: number;
  hourlyPrice?: number;
  maxPricePerBooking?: number;
  duration: number; // in hours
  companyDiscount?: number;
  totalPrice?: number;
  invoiceReference?: string | MultiFieldInvoiceReference;
  notes?: string;
}

export type AccessRole = {
  id: string;
  name: string;
  default: boolean;
  // Whether the logged-in admin may add or remove it, when the server decides
  canEdit?: boolean;
  users?: string[];
  allowedCompanyIds?: string[];
  targetedCompanyIds?: string[];
  buildingId?: string;
};

export type AccessBadge = {
  cardId: string;
  badgeId: string;
  type: CardTypeNew;
  subtype?: CardSubtype;
  state: CardState;
  cardNumber: string;
  deviceType?: VirtualCardDeviceType;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  userId?: string;
  companyId?: string;
};

export enum VirtualCardDeviceType {
  MOBILE = "mobile",
  WATCH = "watch",
}

export enum CardTypeNew {
  PLASTIC = "plastic",
  VIRTUAL = "virtual",
  TEMPORARY = "temporary",
}

export enum CardSubtype {
  DEFAULT = "default",
  APPLE_WALLET = "apple-wallet",
  GOOGLE_WALLET = "google-wallet",
}

export enum CardState {
  ACTIVE = "active",
  DEACTIVATED = "deactivated",
  INACTIVE = "inactive",
  AVAILABLE = "available", //Should never happend for user (available cards are not attached to a particular user)
  ACTIVATING = "activating",
  DEACTIVATING = "deactivating",
}

export type MunuCompanyData = {
  munuId: string;
  munuName: string;
};

export type MunuUserData = {
  AccountType: string;
  BenefitId: number;
  BenefitName: string;
  CardNumber: string;
  DealId: string;
  EmailAddress: string;
  FirstName: string;
  Id: number;
  LastName: string;
  MobilePhone: string;
  QRCode: string;
  Status: string;
  UUID: string;
};

export enum ArticleCategory {
  DEFAULT = "DEFAULT",
  OPERATIONAL = "OPERATIONAL",
}

export enum RecipientGroup {
  COMPANY_ADMINS = "COMPANY_ADMINS",
  ALL = "ALL",
}

export enum NotificationMethod {
  EMAIL = "EMAIL",
  SMS = "SMS",
}

export type Notification = {
  id?: string;
  notificationMethods: NotificationMethod[]; // e.g. ["email"], ["email", "sms"] ... could potentially add Slack in future??
  sentAt?: Timestamp; // when undefined we know the notification is pending (waiting for the post to be published)
  recipients: {
    companiesIds: string[];
    groups: RecipientGroup[];
    lastFetched?: Timestamp; // we can use this to determine if the data is too old and we should re-fetch before notifying
    companyAdmins?: string[]; // array of userIds - this way we can know whether users have been notified multiple times and show correct statistics
    users?: string[]; // same as above
    cannotBeNotified?: string[]; // same as above
    // NB! companyAdmins.length + users.length = total recipients count
  };
};

export type ArticleReadLogItem = {
  userId: string;
  companyId: string;
  isCompanyAdmin: boolean; // should only be true if admin in company they belong to
};

export enum ArticleStatus {
  DRAFT = "draft",
  PLANNED = "planned",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export type Article = {
  id: string;
  createdAt?: Timestamp;
  pinned?: boolean; // true if the article is pinned to the top of the list
  hasRead?: boolean;
  createdByUserId: string; // id of user who clicked "create" - only used for us to track it, don't think it will show up in UI anywhere
  lastUpdated?: {
    // only used if a published post is edited - we then display this timestamp instead of published timestamp
    by: string; // userId for our own records, not shown in UI as of now
    at: Timestamp;
  };
  bannerImg?: string; // url
  authorUserId: string;
  status: ArticleStatus;
  plannedPublishOn?: Timestamp;
  publishedAt?: Timestamp;
  categories: (string | ArticleCategory)[];
  title: {
    no: string;
    en: string;
  };
  content: {
    no: string;
    en: string;
  };
  writtenWithAI?: boolean; // true if the content was generated by AI, false if written manually
  disregardConsent: boolean; // true if content is purely "driftskritisk"
  attachments?: { title: string; url: string }[]; // array of attachment urls
  plannedArchivationOn?: Timestamp;
  archivedAt?: Timestamp;
  recipients: {
    companiesIds?: string[]; // if undefined display news to all companies
    groups?: RecipientGroup[]; // e.g. ["companyAdmin"] - if undefined display news to all users - let's keep as string[] since we might add more groups in future, in which case we can allow more granularity than selecting one group only
    lastFetched: Timestamp; // we can use this to determine if the data is too old and we should re-fetch before publishing
    readLog: ArticleReadLogItem[];
  };
  updates?: {
    timestamp: Timestamp;
    title: string;
    content?: string; // only title is required, in case the update is very short
    authorUserId: string;
  }[];
  statistics?: {
    reach: ArticleReachStatistics;
    read: ArticleReadStatistics;
  };
};

export type AIGeneratedArticleResponse = {
  title: {
    no: string;
    en: string;
  };
  content: {
    no: string;
    en: string;
  };
};

export type AIGeneratedMenuItemResponse = {
  image: ExportImageData;
  name: {
    no: string;
    en: string;
  };
  description: {
    no: string;
    en: string;
  };
};

export type ArticleAuthor = {
  profileImgUrl?: string;
  name: string;
  jobTitle?: string;
  companyName: string;
};

export type ExtendedArticle = Article & {
  author?: ArticleAuthor;
};

export type ArticleReachStatistics = {
  totalBuildingUsers: number;
  recipients: {
    total: number;
    adminsTotal: number;
    regularTotal: number;
  };
  nonRecipients: number;
  lastFetched: Timestamp; // used to determine whether stats are stale and should be refetched
};

export type ArticleReadStatistics = {
  companies: {
    id: string;
    name: string;
    count: number;
    percentage: number;
  }[];
  lastFetched: Timestamp; // used to determine whether stats are stale and should be refetched
};

export type ArticleNotificationReach = {
  userCount: number;
  companyAdminCount: number;
  usersWithoutConsentCount: number;
};

export type ArticleSubmitType = Omit<
  Article,
  "id" | "statistics" | "plannedPublishOn" | "plannedArchivationOn"
> & {
  plannedPublishOn?: Date | null;
  plannedArchivationOn?: Date | null;
};

export type ArticleFormType = Omit<
  ArticleSubmitType,
  "recipients" | "plannedPublishOn" | "plannedArchivationOn"
> & {
  bannerImgUrl?: string;
  recipients: {
    companiesIds?: string[];
    groups?: RecipientGroup[];
    lastFetched: Timestamp;
    readLog: ArticleReadLogItem[];
  };
  plannedPublishOn?: Date | null;
  plannedArchivationOn?: Date | null;
};

export type MultiFieldInvoiceReference = {
  field1: string;
  field2: string;
  field3?: string;
};

export enum SnackTimeframeFilter {
  ALL = "all",
  TODAY = "today",
  TOMORROW = "tomorrow",
  THIS_WEEK = "this_week",
  LAST_MONTH = "last_month",
  CURR_MONTH = "curr_month",
  PAST = "past",
  FUTURE = "future",
  LAST_30_DAYS = "last_30_days",
  LAST_90_DAYS = "last_90_days",
  NEXT_30_DAYS = "next_30_days",
}

export type SnackTimeframeFilterValue =
  SnackTimeframeFilter | FromToDateFilterValue;

export type SnackOrderFormItem = {
  menuItemId: string;
  quantity: number;
  notes?: string;
};

export enum SnackDeliveryMethod {
  PICKUP_WITH_SERVICE = "pickup_with_service",
  PICKUP_WITHOUT_SERVICE = "pickup_without_service",
  DELIVERY_WITH_SERVICE = "delivery_with_service",
  DELIVERY_WITHOUT_SERVICE = "delivery_without_service",
}
export interface SnackOrderFormType {
  companyName: string;
  userId: string;
  items: SnackOrderFormItem[];
  deliveryType: SnackDeliveryMethod | null;
  deliveryTime: Date;
  deliveryInstructions?: string;
  invoiceReference: string | MultiFieldInvoiceReference;
  comment?: string;
  participantsCount?: number;
  supplierAddress?: string;
}

export type LunchVariant = {
  id: string;
  name: string;
  icon: string;
  color: string;
  sortOrder?: number;
};

export type SupplierFoodSettings = {
  deals: FoodDeal[]; // Array of deals available in building
  openingLunchTime: TimeString;
  closingLunchTime: TimeString;
  hasMultipleVariants: boolean;
  lunchVariants: LunchVariant[];
  guestLunchGrossPrice?: number | null; // Positive integer if guest lunch is enabled, null/undefined if disabled
  audit: {
    createdAt: Timestamp;
    updatedAt: Timestamp;
    createdBy: string;
    updatedBy: string;
  };
};

export type SnackDeliverySettings = {
  deliveryFee?: number; // Default delivery fee if delivery is supported
  deliveryOpenAt?: TimeString; // Opening time for deliveries in HH:MM format (e.g., "08:00")
  deliveryClosedAt?: TimeString; // Closing time for deliveries in HH:MM format (e.g., "16:00")
};
export type SnackPickupSettings = {
  pickupFee?: number; // Default pickup fee if pickup is supported
  pickupOpenAt?: TimeString; // Opening time for pickup in HH:MM format (e.g., "08:00")
  pickupClosedAt?: TimeString; // Closing time for pickup in HH:MM format (e.g., "16:00")
  pickupLocation?: string; // Free text where supplier can add location to pickup
};
// format: HH:MM+DD
export type ExpandedCutoffTimeString = `${string}:${string}+${string}` & {
  __brand: "ExpandedCutoffTimeString";
};
/**
 * One cut-off rule, mirroring `CutoffSettings` in wlcm-food
 * (`src/_utils/types.ts`). Notice minutes are always positive; a zero notice
 * is sent as `none`.
 */
export type CutoffSettings =
  | { kind: "none" }
  | { kind: "notice"; minutes: number }
  | {
      kind: "deadline";
      localTime: TimeString;
      /** Oslo calendar days between the deadline and the fulfilment date */
      daysBefore: number;
      calendar: "calendar";
    };

/** Derived by the backend from the global flag; never sent from the client */
export type SnackOrderingMode = "legacy" | "cutoff-v1";

// Order lifecycle wire types, mirroring wlcm-food `meeting-food/order/lifecycleContracts.ts`

/**
 * Derived by the backend from its lifecycle activation; absent on older
 * backends. Absent or "legacy" keeps the existing status display, actions and
 * browser-triggered sweep.
 */
export type SnackLifecycleMode = "legacy" | "delivery-closure-v1";

/** Advisory only: every mutation is re-checked by the backend against current state */
export type SnackOrderCapabilities = {
  canEdit: boolean;
  canMarkDelivered: boolean;
  canClose: boolean;
};

export type SnackLifecycleTransitionSource = "manual" | "automatic";

/** Stored transition metadata; null for events that never happened or predate recording */
export type SnackOrderLifecycle = {
  deliveryMarkedAt: Timestamp | null;
  deliveryMarkedBy: string | null;
  deliveryMarkSource: SnackLifecycleTransitionSource | null;
  closedAt: Timestamp | null;
  closedBy: string | null;
  closureSource: SnackLifecycleTransitionSource | null;
};

// Availability wire types, mirroring wlcm-food `meeting-food/cutoff/contracts.ts`

/** The time, method and items an availability check or order was evaluated for */
export type SnackAvailabilitySelection = {
  /** ISO instant. Responses use UTC with milliseconds */
  deliveryTime: string;
  deliveryType: SnackDeliveryMethod;
  selectedItemIds: string[];
};

/**
 * The ordering boundary for an item. `inclusive` means ordering at exactly
 * `at` is still allowed (minimum notice); deadlines are exclusive.
 */
export type SnackOrderBy = { at: string; inclusive: boolean };

export type SnackTimeProblemCode =
  | "DELIVERY_TIME_IN_PAST"
  | "OUTSIDE_OPERATING_HOURS"
  | "DELIVERY_TYPE_UNSUPPORTED"
  | "SUPPLIER_CLOSED";

export type SnackItemProblemCode =
  "CUTOFF_PASSED" | "ITEM_UNAVAILABLE" | "ITEM_NOT_FOUND";

export type SnackRejectedItem = {
  itemId: string;
  reasons: { code: SnackItemProblemCode }[];
  orderBy: SnackOrderBy | null;
};

/** Body `data` of a 409 `ORDER_NOT_ORDERABLE` from order submission */
export type SnackOrderNotOrderableData = {
  evaluatedAt: string;
  selection: SnackAvailabilitySelection;
  timeProblems: { code: SnackTimeProblemCode }[];
  rejectedItems: SnackRejectedItem[];
};

export type SnackAvailabilityData = SnackOrderNotOrderableData & {
  orderingMode: "cutoff-v1";
  /** Strictly after `evaluatedAt`, or null when no time boundary is coming up */
  nextRefreshAt: string | null;
  /** Only the fields the order form uses; the full item is in the catalog */
  orderableItems: { id: string; orderBy: SnackOrderBy | null }[];
};

export type SnackSupplier = {
  id: string; // Auto-generated document ID
  name: string;
  email: string;
  phone: string;
  address: string;
  isActive: boolean;
  logoUrl?: string;
  buildingId: string; // Building that supplier serves
  orgNumber: string; // Organization number
  externalLocationId?: string;
  deliveryTypes: SnackDeliveryMethod[];
  // Delivery settings
  deliverySettings: SnackDeliverySettings;
  // Pickup settings
  pickupSettings: SnackPickupSettings;
  orderCutoffTime?: ExpandedCutoffTimeString | TimeString | "24h" | null;
  orderingMode?: SnackOrderingMode;
  /** Restaurant default, only present while `orderingMode` is "cutoff-v1" */
  cutoffSettings?: CutoffSettings;
  /**
   * No fulfillment on Saturdays or Sundays (Oslo), and `daysBefore` deadlines
   * count only weekdays. Only sent for cutoff-v1 Snack suppliers.
   */
  closedOnWeekends?: boolean;
  /** Absent on backends without the order lifecycle; absent or "legacy" selects the old flow */
  lifecycleMode?: SnackLifecycleMode;
  autoAcceptOrders?: boolean;
  autoPrepareOrders?: boolean;
  autoCompleteOrders?: boolean;
  wlcmFoodSettings?: SupplierFoodSettings;
  mollieConnected?: boolean;
  // Earliest food transaction date (YYYY-MM-DD), or null when there is no data
  // yet. Cached on the supplier doc; used to bound food date pickers.
  earliestTransactionDate?: string | null;
};

export type SnackSupplierFormType = Omit<
  SnackSupplier,
  | "id"
  | "buildingId"
  | "isActive"
  | "orgNumber"
  | "logoUrl"
  | "orderCutoffTime"
  | "orderingMode"
  | "cutoffSettings"
  | "closedOnWeekends"
  | "lifecycleMode"
  | "wlcmFoodSettings"
> & {
  logo?: {
    previewUrl: string;
    encoded?: string;
  };
  orderCutoffTime: ExpandedCutoffTimeString | TimeString | "24h" | null;
  cutoffSettings: CutoffSettings;
  /** Absent on the supplier means open every day */
  closedOnWeekends: boolean;
  wlcmFoodSettings: Partial<SupplierFoodSettings>;
};

export type SnackCategory = {
  id: string;
  name: {
    en: string;
    no: string;
  };
  sortOrder: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  linkedItemsCount: number;
  hiddenLinkedItemsCount: number;
  isHidden: boolean; // true if all linked items are hidden, false otherwise
  bumpVatToHighRate?: boolean;
};

export type SnackCategorySubmitType = Pick<
  SnackCategory,
  "id" | "name" | "sortOrder" | "bumpVatToHighRate"
>;

export type SnackMenu = {
  id: string;
  name: string;
  email: string;
  categories: SnackCategory[];
  items: SnackMenuItem[];
};

export type SnackMenuItem = {
  id: string;
  menuId: string;
  price: number;
  isAvailable: boolean;
  minOrderQuantity?: number | null;
  maxOrderQuantity?: number | null;
  name: {
    en: string;
    no: string;
  };
  description: {
    en: string;
    no: string;
  };
  categoryId: string;
  allergens: string[];
  imageUrl: string[];
  /**
   * The item's own override, or null when it follows the restaurant default.
   * The cut-off fields are only present in "cutoff-v1" mode.
   */
  cutoffSettings?: CutoffSettings | null;
  effectiveCutoffSettings?: CutoffSettings;
  cutoffSource?: "supplier" | "item";
};

export type SnackMenuItemFormType = Omit<
  SnackMenuItem,
  | "id"
  | "menuId"
  | "imageUrl"
  | "categoryId"
  | "cutoffSettings"
  | "effectiveCutoffSettings"
  | "cutoffSource"
> & {
  categoryId?: string;
  cutoffSettings: CutoffSettings | null;
  image?: {
    previewUrl: string;
    encoded?: string;
  } | null;
};

export enum SnackOrderStatus {
  ORDERED = "ORDERED",
  CONFIRMED = "CONFIRMED",
  PREPARING = "PREPARING",
  /** Sent and stored only while the backend lifecycle is "delivery-closure-v1" */
  DELIVERED = "DELIVERED",
  DENIED = "DENIED",
  CANCELLED = "CANCELLED",
  /** The stored terminal value; displayed as Closed once the lifecycle is active */
  COMPLETED = "COMPLETED",
}

export type SnackOrderItemHistoryEntry = {
  id: string;
  quantity: number;
  notes?: string;
};

export type SnackOrderEditHistoryItemChange =
  | {
      field: "items";
      action: "ITEMS_UPDATED";
      old: SnackOrderItemHistoryEntry[];
      new: SnackOrderItemHistoryEntry[];
    }
  | {
      field: "deliveryType";
      action: "DELIVERY_TYPE_UPDATED";
      old: SnackOrder["deliveryType"];
      new: SnackOrder["deliveryType"];
    }
  | {
      field: "deliveryTime";
      action: "DELIVERY_TIME_UPDATED";
      old: SnackOrder["deliveryTime"];
      new: SnackOrder["deliveryTime"];
    }
  | {
      field: "participantsCount";
      action: "PARTICIPANTS_COUNT_UPDATED";
      old: SnackOrder["participantsCount"];
      new: SnackOrder["participantsCount"];
    }
  | {
      field: "invoiceReference";
      action: "INVOICE_REFERENCE_UPDATED";
      old: SnackOrder["invoiceReference"];
      new: SnackOrder["invoiceReference"];
    };

export type SnackOrderEditHistoryItem = {
  editedAt: Timestamp;
  editedBy: string; // email
  changes: SnackOrderEditHistoryItemChange[];
};

export type SnackOrder = {
  id: string;
  orderNumber: string;
  buildingId: string;
  supplierId: string;
  userId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  companyId: string;
  companyName: string;
  userLanguage: string;
  items: SnackOrderItem[];
  subtotal: number;
  deliveryFee: number;
  pickupFee?: number;
  totalAmount: number;
  vatRate?: number;
  vatAmount?: number;
  netAmount?: number;
  deliveryType: SnackDeliveryMethod;
  status: {
    current: SnackOrderStatus;
    updatedAt: Timestamp;
    updatedBy: string;
    notes?: string;
  };
  orderAt?: Timestamp;
  deliveryAddress?: string;
  deliveryInstructions?: string;
  invoiceReference?: string | MultiFieldInvoiceReference;
  deliveryTime: Timestamp;
  comment?: string;
  participantsCount?: number;
  originalOrder?: {
    items: SnackOrderItem[];
    subtotal: number;
    totalAmount: number;
    deliveryTime: Timestamp;
    invoiceReference: string | MultiFieldInvoiceReference;
    participantsCount: number;
  };
  editHistory?: SnackOrderEditHistoryItem[];
  /** Absent on backends without the lifecycle; absent or "legacy" selects the old flow */
  lifecycleMode?: SnackLifecycleMode;
  /** Only in "delivery-closure-v1": ISO instant the closure deadline passes, null in manual mode */
  automaticCloseAt?: string | null;
  /** Only in "delivery-closure-v1" */
  capabilities?: SnackOrderCapabilities;
  /** Only in "delivery-closure-v1" */
  lifecycle?: SnackOrderLifecycle;
  /** An invalid list row remains visible but offers no lifecycle actions. */
  lifecycleDiagnostic?: {
    code: "ORDER_LIFECYCLE_DATA_INVALID";
    diagnostic: "MISSING_DELIVERY_TIME" | "INVALID_DELIVERY_TIME";
  };
};

/**
 * The success body of `PUT /updateSnackOrderStatus`: the committed status plus
 * the lifecycle fields projected from the committed order (absent on backends
 * without the lifecycle). Merged into the cached order so the page shows the
 * committed result before the refetch lands.
 */
export type SnackOrderStatusUpdate = Pick<
  SnackOrder,
  | "id"
  | "orderNumber"
  | "lifecycleMode"
  | "automaticCloseAt"
  | "capabilities"
  | "lifecycle"
> & {
  status: SnackOrderStatus;
  statusUpdatedAt: Timestamp;
  statusNotes?: string;
};

/**
 * The success body of `PUT /updateSnackOrder`: the committed order projection
 * plus the lifecycle fields (absent on backends without the lifecycle). In the
 * active lifecycle an edit whose accepted schedule's closing time has already
 * passed reports `status.current === COMPLETED`: the save also closed the
 * order. Merged into the cached order so the page shows the committed result
 * before the refetch lands.
 */
export type SnackOrderEditResult = Pick<
  SnackOrder,
  | "id"
  | "orderNumber"
  | "items"
  | "subtotal"
  | "deliveryFee"
  | "totalAmount"
  | "vatRate"
  | "vatAmount"
  | "netAmount"
  | "deliveryType"
  | "status"
  | "deliveryAddress"
  | "invoiceReference"
  | "deliveryTime"
  | "participantsCount"
  | "originalOrder"
  | "editHistory"
  | "lifecycleMode"
  | "automaticCloseAt"
  | "capabilities"
  | "lifecycle"
>;

export type SnackAdminOrder = SnackOrder & {
  orderBy: {
    name: string;
    email: string;
  };
};

export type PrintableSnackOrder = SnackOrder & {
  userPhone: string;
};

export type SnackOrderItem = {
  id: string;
  name: { en: string; no: string };
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  specialPrice?: number;
  preparationTime: number;
  portion?: number;
  notes?: string;
};

export type TranslationMeta =
  | {
      translatedField?: "en" | "no";
      lastChangedField?: "en" | "no";
    }
  | undefined;

export type MillumDish = {
  itemNumber: string;
  name: string;
  imageType?: string;
  imageBlob?: string;
  allergens?: string[];
  tracesOf?: string[];
};

export type MillumDailyMenu = {
  /**
   * Date in DD-MM-YYYY format
   */
  activeDate: string;
  dishes: MillumDish[];
};

export type MillumWeeklyMenu = {
  name: string;
  week: number;
  dates: MillumDailyMenu[];
};

export type MillumAllergen = {
  code: string;
  name: string;
  allergenGroup: string;
  order: number;
};

export type AdminTableFooterDescription = {
  icon: string;
  text: string;
};

export type ExportImageData = {
  previewUrl: string;
  encoded: string;
  raw: Blob;
};

export type UploadTemplateAssetsPayload = {
  templateName: string;
  frontHtml: string; // base64 string
  backHtml?: string; // base64 string
  cssFile?: string; // base64 string
  images: {
    backgroundImageFront?: string; // base64 string
    backgroundImageBack?: string; // base64 string
  };
  assignedToCompanies?: string[];
  assignedToAllCompanies?: boolean;
};

export interface BaseTemplateType {
  templateName: string;
  assignedToCompanies: string[];
  assignedToAllCompanies: boolean;
}

export type BadgeTemplateMetadata = BaseTemplateType & {
  templateId: string;
  hasBack: boolean;
  hasCss: boolean;
  templateStoragePath: string;
  createdAt: Timestamp;
  createdBy: string; // user’s id
  backgroundImageFrontExtension?: string;
  backgroundImageBackExtension?: string;
};

export type TemplateAssetsResponse = {
  metadata: BadgeTemplateMetadata;
  assets: {
    frontHtml: string;
    backHtml?: string;
    css?: string;
    backgroundImageFront?: string; // base64 string
    backgroundImageBack?: string; // base64 string
  };
};

export type TemplateFieldsValidationType = {
  userId: string;
  buildingTenantId: string;
  fullName: string;
  issues: {
    key: string;
    templateToken: string;
    variable: string;
  }[];
};

export type TemplateFormType = BaseTemplateType & {
  frontHtml: File | null;
  backHtml?: File | null;
  stylesCss?: File | null;
  backgroundImageFront?: ExportImageData | null;
  backgroundImageBack?: ExportImageData | null;
};

export type CompanyLookupResponse = {
  status: "EXISTS_IN_BUILDING" | "MATCH_ORG_GLOBAL" | "NO_MATCH";
  matchedCompany?: CompanyLookupMatch;
};

export type CompanyLookupMatch = Pick<
  Company,
  | "buildingTenantId"
  | "buildingTenantName"
  | "companyLogoImgUrl"
  | "orgNumber"
  | "ssoSettings"
  | "companyType"
>;

export enum StarwatchAccessGroupStatus {
  ACTIVE = "active",
  REMOVED = "removed",
}

// A Starwatch role, synced from Starwatch; Welcome roles are created from it.
export type StarwatchAccessGroup = {
  groupId: number;
  groupName: string;
  status: StarwatchAccessGroupStatus;
  lastSyncedAt: string | null;
};

export type StarwatchRoleMapping = {
  targetedCompanyIds: string[];
  targetsAllCompanies: boolean;
  allowedCompanyIds: string[];
  allowsAllCompanies: boolean;
  isDefault: boolean;
};

// Shown with its group's name; groupName and groupStatus are null when the
// group is missing from the catalogue.
export type StarwatchAccessRole = StarwatchRoleMapping & {
  roleId: string;
  accessGroupId: number;
  groupName: string | null;
  groupStatus: StarwatchAccessGroupStatus | null;
  createdAt: string | null;
  updatedAt: string | null;
};

// A role on a user's access page: one they hold or one their company can
// receive, with whether the logged-in admin may add or remove it.
export type StarwatchUserAccessRole = {
  roleId: string;
  accessGroupId: number;
  groupName: string | null;
  groupStatus: StarwatchAccessGroupStatus | null;
  isDefault: boolean;
  assigned: boolean;
  canEdit: boolean;
  allowedCompanyIds: string[];
  allowsAllCompanies: boolean;
};

export type AccessRoleOverviewItem = {
  id: string;
  name: string;
  isDefault: boolean;
  targetedCompanies: { id: string; isVisitorCompanyRole: boolean }[];
  allowedCompanies: { id: string; isVisitorCompanyRole: boolean }[];
  allTargetedCompanies: boolean;
  allAllowedCompanies: boolean;
  isVisitorsRole: boolean;
};

export type AccessLevel = {
  id: string;
  name: string;
};

export type AccessRolePayload = {
  accessRoleName: string;
  accessLevelId: string;
  isDefault: boolean;
  forEmployees: boolean;
  forVisitors: boolean;
  targetedCompanyIds: string[];
  allowedCompanyIds: string[];
};

export type IntegratedCompany = Pick<
  Company,
  "buildingTenantId" | "buildingTenantName"
>;

export type UpdateAccessRolePayload = Omit<
  AccessRolePayload,
  "accessRoleName" | "accessLevelId"
> & {
  accessRoleId: string;
};

export type UserAssignedToRole = {
  email: string;
  companyName: string;
};

export type DeleteRoleConflictError = Error & {
  statusCode: 409;
  users: UserAssignedToRole[];
};
